/**
 * Every conversion the app performs, in one place, so none is improvised at
 * a call site (§11.3).
 *
 * The governing rule: **extracted values are never reformatted.** The
 * printed form is the stored string, untouched; the western form is
 * `value_western`, also as stored. Reformatting is exactly the operation
 * that would hide a transliteration error, which is one of the things this
 * app exists to surface.
 *
 * Only *computed* figures -- counts, rates, the typed `value` -- go through
 * Intl, and those are forced to Latin digits even in Arabic UI: a computed
 * figure shown in Arabic-indic digits is indistinguishable from a printed
 * one.
 */
export type Locale = 'en' | 'ar'

const numberFmt = new Map<string, Intl.NumberFormat>()
function nf(locale: Locale, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = locale + JSON.stringify(opts)
  let f = numberFmt.get(key)
  if (!f) {
    // `numberingSystem: 'latn'` on purpose, in both languages.
    f = new Intl.NumberFormat(locale === 'ar' ? 'ar-SA-u-nu-latn' : 'en-US', {
      numberingSystem: 'latn',
      ...opts,
    })
    numberFmt.set(key, f)
  }
  return f
}

/** A count. Always Latin digits, always grouped. */
export function count(n: number | null | undefined, locale: Locale = 'en'): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  return nf(locale, { maximumFractionDigits: 0 }).format(n)
}

/**
 * The typed `value` column, which arrives as a numeric string. Formatted for
 * reading, never parsed for arithmetic.
 */
export function money(v: string | number | null | undefined, locale: Locale = 'en'): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = typeof v === 'number' ? v : Number(v)
  if (!Number.isFinite(n)) return String(v)
  return nf(locale, { maximumFractionDigits: 2 }).format(n)
}

/**
 * A rate, to one decimal, and **always with its denominator nearby** at the
 * call site. A rate without its base has caused more misreadings of this
 * dataset than any other single thing.
 */
export function percent(
  r: number | null | undefined,
  locale: Locale = 'en',
  digits = 1,
): string {
  if (r === null || r === undefined || !Number.isFinite(r)) return '—'
  return nf(locale, {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(r)
}

/** Whole-percent, for chips and dots where one decimal is noise. */
export const percent0 = (r: number | null | undefined, locale: Locale = 'en') =>
  percent(r, locale, 0)

const dateFmt = new Map<string, Intl.DateTimeFormat>()

/**
 * `period_end` and friends arrive as `YYYY-MM-DD` strings and are formatted
 * as such -- never routed through `new Date(s)`, which would land at UTC
 * midnight and render as the previous day west of Greenwich.
 */
export function date(iso: string | null | undefined, locale: Locale = 'en'): string {
  if (!iso) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  const key = locale + 'd'
  let f = dateFmt.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB', {
      // Gregorian in both languages, because that is what the filings state.
      calendar: 'gregory',
      numberingSystem: 'latn',
      year: 'numeric', month: 'short', day: 'numeric',
      timeZone: 'UTC',
    })
    dateFmt.set(key, f)
  }
  // Constructed in UTC and formatted in UTC: the two cancel, so the day that
  // comes out is the day that went in.
  return f.format(new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!)))
}

/**
 * `created_at` / `updated_at` are timestamptz. Rendered in the browser's
 * zone *with the zone named*, since the pipeline records host time and the
 * reader may not be in it.
 */
export function timestamp(iso: string | null | undefined, locale: Locale = 'en'): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const key = locale + 't'
  let f = dateFmt.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB', {
      calendar: 'gregory', numberingSystem: 'latn',
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    })
    dateFmt.set(key, f)
  }
  return f.format(d)
}

/** Milliseconds, at the precision a person reads them at. */
export function duration(ms: number | null | undefined, locale: Locale = 'en'): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—'
  if (ms < 1000) return `${Math.round(ms)} ms`
  if (ms < 60_000) return `${nf(locale, { maximumFractionDigits: 1 }).format(ms / 1000)} s`
  const min = Math.floor(ms / 60_000)
  const sec = Math.round((ms % 60_000) / 1000)
  return `${min}m ${String(sec).padStart(2, '0')}s`
}

/** Byte counts, for the PDF size and nothing else. */
export function bytes(n: number | null | undefined, locale: Locale = 'en'): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let v = n
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${nf(locale, { maximumFractionDigits: v < 10 && i > 0 ? 1 : 0 }).format(v)} ${units[i]}`
}
